import React, { useState } from 'react';
import { api } from '../api';
import { AuthUser } from '../types';
import { tg } from '../telegram';
import { Coffee, Lock, User, Eye, EyeOff, ShieldCheck, UserCheck, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface LoginScreenProps {
  onLoginSuccess: (user: AuthUser) => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error('Vui lòng nhập tên đăng nhập');
      return;
    }
    if (!password.trim()) {
      toast.error('Vui lòng nhập mật khẩu');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    tg.haptic.impact('medium');

    try {
      const res = await api.login({
        username: username.trim(),
        password: password.trim(),
      });

      if (res.success && res.user) {
        tg.haptic.success();
        toast.success(`Xin chào, ${res.user.name}!`);
        onLoginSuccess(res.user);
      } else {
        tg.haptic.error();
        const err = res.error || 'Tên đăng nhập hoặc mật khẩu không chính xác';
        setErrorMessage(err);
        toast.error(err);
      }
    } catch (err: any) {
      tg.haptic.error();
      const errText = err?.message || 'Không thể kết nối đến máy chủ. Vui lòng thử lại.';
      setErrorMessage(errText);
      toast.error(errText);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px 16px', maxWidth: '440px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Brand Header */}
      <div style={{ textAlign: 'center', padding: '16px 0 8px 0' }}>
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #7A4628 0%, #3C2114 100%)',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            boxShadow: '0 8px 20px rgba(60, 33, 20, 0.28)',
          }}
        >
          <Coffee size={32} />
        </div>
        <h1 style={{ fontSize: '24px', fontWeight: '800', letterSpacing: '-0.02em', color: 'var(--text-primary)', marginBottom: '6px' }}>
          Bar Ingredients
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
          Hệ thống kiểm soát định lượng & tồn kho nguyên liệu quầy Bar
        </p>
      </div>

      {/* Login Card */}
      <div className="card" style={{ padding: '24px' }}>
        <h2 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Lock size={18} color="var(--primary-accent)" />
          Đăng Nhập Hệ Thống
        </h2>

        {errorMessage && (
          <div
            style={{
              padding: '12px 14px',
              backgroundColor: 'var(--color-danger-bg)',
              color: 'var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px',
            }}
          >
            <AlertCircle size={18} style={{ flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Tên đăng nhập
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="input"
                placeholder="admin hoặc staff"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                disabled={isLoading}
                style={{ paddingLeft: '40px' }}
              />
              <User
                size={18}
                color="var(--text-tertiary)"
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Mật khẩu
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="input"
                placeholder="Nhập mật khẩu..."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                style={{ paddingLeft: '40px', paddingRight: '42px' }}
              />
              <Lock
                size={18}
                color="var(--text-tertiary)"
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-tertiary)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={isLoading}
            style={{ marginTop: '8px' }}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Đang xác thực...
              </>
            ) : (
              'Đăng Nhập'
            )}
          </button>
        </form>
      </div>

      {/* Role Explanations */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div
          style={{
            padding: '12px 14px',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            fontSize: '12px',
            lineHeight: 1.5,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', color: 'var(--primary-accent)', marginBottom: '4px' }}>
            <ShieldCheck size={16} />
            Quản lý / Chủ quán (ADMIN)
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            Toàn quyền L1-L5, đồng bộ Master Data, quản lý kho tổng và phân tích sai lệch.
          </div>
        </div>

        <div
          style={{
            padding: '12px 14px',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            fontSize: '12px',
            lineHeight: 1.5,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', color: 'var(--color-success)', marginBottom: '4px' }}>
            <UserCheck size={16} />
            Nhân viên ca / Barista (STAFF)
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            Thao tác vận hành quầy: Nhận chuyển kho (L2), Chốt ca (L3), Báo hủy hỏng (L4), Kiểm kê tồn (L5).
          </div>
        </div>
      </div>
    </div>
  );
};
